import React, { useContext, useEffect, useState } from 'react';
import { AlertCircle, Camera, ChevronRight, Keyboard, Users } from 'lucide-react';
import QRScanner from '../../components/scanner/QRScanner';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../../Context/Brand/BrandContext';
import { getMerchantProfile } from '../../services/merchant';
import { getPublicTableByNumber } from '../../services/table';
import { getMyActiveTableSessions } from '../../services/tableSession';
import TableSessionContext from '../../Context/TableSession/TableSessionContext';
import { usePlatformBrand } from '../../Context/PlatformBrand/PlatformBrandContext';

const QRCodeScanner = () => {
  const platformBrand = usePlatformBrand();
  const [scanning, setScanning] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manualId, setManualId] = useState('');
  const [manualTable, setManualTable] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [tableError, setTableError] = useState('');
  const [activeSessions, setActiveSessions] = useState([]);
  const [resumingId, setResumingId] = useState('');
  const navigate = useNavigate();
  const { loadBrand } = useBrand();
  const { resume } = useContext(TableSessionContext);

  const accentColor = '#0D3031';

  useEffect(() => {
    if (!localStorage.getItem('tokens')) return;
    getMyActiveTableSessions()
      .then((response) => setActiveSessions(response?.data?.sessions || []))
      .catch(() => setActiveSessions([]));
  }, []);

  const returnToSession = async (openSession) => {
    setResumingId(openSession.sessionId);
    try {
      const restored = await resume(openSession.sessionId);
      localStorage.setItem('merchant_id', restored.restaurantId);
      localStorage.setItem('table_number', restored.tableRef);
      loadBrand(restored.restaurantId);
      navigate('/order', {
        state: {
          merchantId: restored.restaurantId,
          table_number: restored.tableRef,
          table_session_id: restored.session?.id,
        },
      });
    } catch (error) {
      setTableError(error?.response?.data?.error || error?.message || 'Could not return to that table.');
      setActiveSessions((current) => current.filter((entry) => entry.sessionId !== openSession.sessionId));
    } finally {
      setResumingId('');
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    const id = manualId.trim();
    if (!id) return;

    setSubmitting(true);
    setNotFound(false);
    setTableError('');

    try {
      const res = await getMerchantProfile(id);
      const restaurant = res?.data?.restaurant;
      if (!restaurant?.id) {
        setNotFound(true);
        return;
      }
      let tableNumber = '0';
      const enteredTable = manualTable.trim();
      if (enteredTable) {
        try {
          const tableResult = await getPublicTableByNumber(restaurant.id, enteredTable);
          const table = tableResult?.data?.table;
          if (!table?.table_number) {
            setTableError('That table was not found. Check the label and try again.');
            return;
          }
          tableNumber = table.table_number;
        } catch (error) {
          setTableError(error?.response?.data?.message || error?.response?.data?.error || 'That table was not found. Check the label and try again.');
          return;
        }
      }
      loadBrand(restaurant.id);
      navigate(`/menu/${restaurant.id}/${encodeURIComponent(tableNumber)}/default`);
    } catch {
      setNotFound(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCodeChange = (value) => {
    setManualId(value.replace(/\D/g, '').slice(0, 6));
    setNotFound(false);
  };

  return (
    <div className="min-h-screen bg-[#f5f3ee] text-neutral-950">
      <div className="mx-auto flex min-h-screen w-full max-w-md flex-col px-5 pb-6 pt-10">
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={platformBrand.logoUrl || "/supu-mark.svg"} alt={platformBrand.name} className="h-11 w-11 rounded-xl object-cover" />
            <div>
              <p className="text-sm font-semibold leading-5 text-neutral-950">{platformBrand.name} ordering</p>
              <p className="text-xs text-neutral-500">Scan from your table</p>
            </div>
          </div>
        </header>

        <main className="flex flex-1 flex-col justify-center py-10">
          {activeSessions.length > 0 && (
            <section className="mb-8 border-y border-neutral-300 py-3">
              {activeSessions.slice(0, 3).map((openSession) => (
                <button
                  key={openSession.sessionId}
                  type="button"
                  onClick={() => returnToSession(openSession)}
                  disabled={Boolean(resumingId)}
                  className="flex w-full items-center gap-3 py-2 text-left disabled:opacity-60"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0D3031] text-white">
                    <Users className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[11px] font-semibold uppercase text-neutral-500">Return to your table</span>
                    <span className="mt-0.5 block truncate text-sm font-semibold text-neutral-950">
                      {openSession.restaurantName} · Table {openSession.tableRef}
                    </span>
                  </span>
                  <ChevronRight className={`h-4 w-4 shrink-0 text-neutral-500 ${resumingId === openSession.sessionId ? 'animate-pulse' : ''}`} />
                </button>
              ))}
            </section>
          )}
          <section className="mb-8">
            <h1 className="max-w-[310px] text-4xl font-semibold leading-[1.05] tracking-tight text-neutral-950">
              Scan the table QR code.
            </h1>
            <p className="mt-4 max-w-[310px] text-[15px] leading-6 text-neutral-600">
              Use the QR code on your table. If scanning is not working, enter the restaurant code below.
            </p>
          </section>

          <button
            type="button"
            onClick={() => setScanning(true)}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl text-sm font-semibold text-white transition active:scale-[0.99]"
            style={{ backgroundColor: accentColor }}
          >
            <Camera className="h-4 w-4" />
            Scan QR code
          </button>

          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-neutral-200" />
            <span className="text-xs font-medium text-neutral-400">or</span>
            <div className="h-px flex-1 bg-neutral-200" />
          </div>

          <button
            type="button"
            onClick={() => setShowManual((value) => !value)}
            className="flex h-11 w-full items-center justify-center gap-2 rounded-2xl border border-neutral-300 bg-transparent text-[13px] font-semibold text-neutral-800 transition active:scale-[0.99]"
          >
            <Keyboard className="h-4 w-4" />
            {showManual ? 'Hide code entry' : 'Enter restaurant code'}
          </button>

          {showManual && (
            <form onSubmit={handleManualSubmit} className="mt-6">
              <div className="space-y-4">
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label className="text-xs font-semibold text-neutral-600">Restaurant code</label>
                    {manualId && (
                      <button
                        type="button"
                        onClick={() => handleCodeChange('')}
                        className="text-xs font-medium text-neutral-400 hover:text-neutral-700"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={manualId}
                    onChange={(e) => handleCodeChange(e.target.value)}
                    placeholder="6 digit code"
                    className="h-11 w-full rounded-2xl border border-neutral-300 bg-white px-4 text-center text-lg font-semibold tracking-[0.24em] text-neutral-950 outline-none transition placeholder:text-sm placeholder:font-normal placeholder:tracking-normal focus:border-neutral-500"
                    autoComplete="off"
                  />
                  {notFound && (
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-red-600">
                      <AlertCircle className="h-3.5 w-3.5" />
                      Restaurant code was not found.
                    </p>
                  )}
                </div>

                <div>
                  <label className="mb-2 block text-xs font-semibold text-neutral-600">Table number <span className="font-normal text-neutral-400">optional</span></label>
                  <div className="flex h-11 items-center rounded-2xl border border-neutral-300 bg-white px-3 focus-within:border-neutral-500">
                    <input
                      type="text"
                      inputMode="text"
                      maxLength={20}
                      value={manualTable}
                      onChange={(e) => {
                        setManualTable(e.target.value.replace(/[^a-zA-Z0-9 _-]/g, '').slice(0, 20).toUpperCase());
                        setTableError('');
                      }}
                      placeholder="For example T-17"
                      className="h-full w-full bg-transparent text-center text-base font-semibold text-neutral-950 outline-none placeholder:text-neutral-300"
                      autoCapitalize="characters"
                      autoComplete="off"
                    />
                  </div>
                  {tableError && (
                    <p className="mt-2 flex items-start gap-1.5 text-xs leading-5 text-red-600">
                      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      {tableError}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={manualId.length !== 6 || submitting}
                  className="flex h-11 w-full items-center justify-center rounded-2xl text-[13px] font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-40 active:scale-[0.99]"
                  style={{ backgroundColor: accentColor }}
                >
                  {submitting ? 'Checking code...' : 'Continue'}
                </button>
              </div>
            </form>
          )}
        </main>

        <p className="text-center text-xs leading-5 text-neutral-400">
          Ask a team member if your table does not have a code.
        </p>
      </div>

      {scanning && <QRScanner onClose={() => setScanning(false)} />}
    </div>
  );
};

export default QRCodeScanner;
